"use client";

import ChannelsList, { IChannel, useChannels } from "./chat/ChannelsList";
import Conversation from "./chat/Conversation";
import { useEffect, useState } from "react";
import {
  Box,
  Container,
  DropdownMenu,
  Flex,
  Heading,
  IconButton,
} from "@radix-ui/themes";
import SafeRouter from "../utilities/SafeRouter";
import { MessagingListRulesQuery } from "../modals/Messaging/MessagingListRules";
import { ListFilter, OrgFilter } from "./chat/ListFilter";
import FontAwesome from "../utilities/FontAwesome";

export function MessagingWrapper(props: {
  initialChannelId: number | null;
  initialChannels: {
    channels: IChannel[];
    total: number;
  };
  recipientUsers: IUser[];
}) {
  const [currentChannel, setCurrentChannel] = useState<IChannel | undefined>(
    props.initialChannels.channels.find(
      (channel) => channel.id === props.initialChannelId,
    ),
  );

  const [recipientUsers, setRecipientUsers] = useState<IUser[]>(
    props.recipientUsers,
  );
  const [recipientRules, setRecipientRules] = useState<
    MessagingListRulesQuery[]
  >([]);

  const [showConversation, setShowConversation] = useState(
    !!(currentChannel?.id || recipientUsers.length > 0),
  );

  const [filterOrganization, setFilterOrganization] =
    useState<OrgFilter | null>(null);

  const { data, isLoading, setSize, isValidating, mutate } = useChannels(
    props.initialChannels,
    filterOrganization?.id,
  );

  const channels =
    data?.reduce((acc, curr) => [...acc, ...curr.channels], []) || [];
  const total = data ? data[0].total : 0;

  useEffect(() => {
    // purge messaging-related queries after mount so they don't impact future actions
    SafeRouter.setQueries(["uid"], ["listId"], ["role"]);
  }, []);

  function startConversation() {
    setRecipientUsers([]);
    setRecipientRules([]);
    setCurrentChannel(undefined);
    setShowConversation(true);
  }

  async function markChannelAsRead(channelId) {
    if (data) {
      await mutate(
        data.map((page) => ({
          ...page,
          channels: page.channels.map((c) => {
            if (c.id === channelId) {
              return { ...c, has_unread: false };
            } else {
              return c;
            }
          }),
        })),
      );
    }
  }

  return (
    <Container pb={"4"}>
      <Box py={"4"}>
        <DropdownMenu.Root>
          <Flex align={"center"} gapX={"2"}>
            <DropdownMenu.Trigger>
              <IconButton radius={"full"} variant={"surface"} color={"gray"}>
                <FontAwesome prefix={"fas"} name={"fa-bars"} />
              </IconButton>
            </DropdownMenu.Trigger>
            <Heading size={"7"}>Messaging</Heading>
          </Flex>
          <DropdownMenu.Content variant={"soft"}>
            <ListFilter
              setFilterOrganization={async (value) => {
                setFilterOrganization(value);
              }}
              filterOrganization={filterOrganization}
            />
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </Box>
      <Flex
        p={"1"}
        gapX={"2"}
        align={"stretch"}
        style={{ margin: "0 auto" }}
        wrap={{
          initial: "wrap",
          md: "nowrap",
        }}
      >
        <ChannelsList
          currentChannel={currentChannel}
          channels={channels}
          setCurrentChannel={setCurrentChannel}
          totalChannels={total}
          showConversation={showConversation}
          setRecipientUsers={setRecipientUsers}
          setRecipientRules={setRecipientRules}
          setShowConversation={setShowConversation}
          startConversation={startConversation}
          isLoadingChannels={isLoading}
          isValidatingChannels={isValidating}
          setChannelsSize={setSize}
          mutateChannels={mutate}
          filterOrganization={filterOrganization}
          markChannelAsRead={markChannelAsRead}
        />
        {showConversation && (
          <Conversation
            setShowConversation={setShowConversation}
            key={currentChannel?.id}
            recipients={recipientUsers}
            recipientRules={recipientRules}
            setRecipientUsers={setRecipientUsers}
            setRecipientRules={setRecipientRules}
            currentChannel={currentChannel}
            setCurrentChannel={setCurrentChannel}
            mutateChannels={mutate}
          />
        )}
      </Flex>
    </Container>
  );
}
